// Favourite devices for quick access. Uses the same inline control cards as the
// room grid — 1/2/4-touch show their buttons, fan shows its dial, etc. Device
// types without an inline card open the full control screen.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, AppBar, Toolbar, Grid, IconButton,
  Checkbox, FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getFavouriteDevices } from '../../../core/api/api';
import { useApp } from '../../../core/state/AppContext';
import { useHideOffline, useOnlineOnly } from '../../../core/hooks/useHideOffline';
import DeviceControlCard from '../components/DeviceControlCard';
import { deviceGridSpan } from '../../../core/devices/deviceMeta';
import { sortByOrder } from '../../../core/utils/sortOrder';

export default function FavouritesScreen() {
  const { userId } = useApp();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchFavs = async (silent) => {
      try {
        const res = await getFavouriteDevices(userId);
        // Shared with the mobile tab and with AppContext's copy of this list —
        // one rule, in core/utils/sortOrder.
        const arr = sortByOrder(Array.isArray(res.data) ? res.data : []);
        if (alive) setDevices(arr);
      } catch (e) {
        /* ignore */
      } finally {
        if (alive && !silent) setLoading(false);
      }
    };
    fetchFavs(false);
    // Poll every 30s so the online/offline dots stay current (DB updates ~30s).
    const id = setInterval(() => fetchFavs(true), 30000);
    return () => { alive = false; clearInterval(id); };
  }, [userId]);

  // Same "Hide offline devices" flag as Home and the mobile shell — one setting,
  // so the answer doesn't change as the user moves between pages.
  const [hideOffline, setHideOffline] = useHideOffline();
  const { devices: shown } = useOnlineOnly(devices, hideOffline);

  // Favourites span multiple locations, so show a "Location - Room" breadcrumb
  // (a device with no room is a location-level common device).
  const breadcrumb = (d) =>
    `${d.LocationName || 'No Location'} - ${d.RoomName || 'Common Device'}`;

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>Favourites</Typography>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={(
              <Checkbox
                size="small"
                color="default"
                checked={hideOffline}
                onChange={(e) => setHideOffline(e.target.checked)}
                sx={{ color: 'inherit' }}
              />
            )}
            label={<Typography variant="body2">Hide offline devices</Typography>}
          />
        </Toolbar>
      </AppBar>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : devices.length === 0 ? (
        <Typography sx={{ textAlign: 'center', mt: 6 }} color="text.secondary">No favourite devices</Typography>
      ) : shown.length === 0 ? (
        <Typography sx={{ textAlign: 'center', mt: 6 }} color="text.secondary">All favourite devices are offline</Typography>
      ) : (
        <Grid container spacing={1.25} alignItems="stretch" sx={{ p: 1.5 }}>
          {shown.map((d) => (
            <Grid item {...deviceGridSpan(d.DeviceID)} key={d.UniqueNumber} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%' }}>
                <DeviceControlCard device={d} subtitle={breadcrumb(d)} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
